import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowSquareOut, Link } from '@phosphor-icons/react';
import { entityHoverCardRegistry } from './EntityHoverRegistry';
import { entityService } from '../../services/entityService';
import { entityLinkService } from '../../services/entityLinkService';
import { getCapability } from '../../utils/entityLinkCapabilities';
import { useEntityIcon } from '../../hooks/useEntityIcon';
import { useCommandRegistryStore } from '../../stores/commandRegistryStore';
import './EntityHoverCard.scss';

const CARD_WIDTH = 300;
const GAP = 6;
const PADDING = 8;

// ---------------------------------------------------------------------------
// Generic fallback card
// ---------------------------------------------------------------------------

const SKIP_SKELETON_KEYS = new Set(['base64', 'content', 'body', 'rawHtml', 'html']);
const MAX_SKELETON_FIELDS = 3;

function readableSkeletonFields(skeleton: Record<string, any>): Array<{ label: string; value: string }> {
  const fields: Array<{ label: string; value: string }> = [];
  for (const [key, val] of Object.entries(skeleton)) {
    if (fields.length >= MAX_SKELETON_FIELDS) break;
    if (SKIP_SKELETON_KEYS.has(key)) continue;
    if (val === null || val === undefined) continue;
    if (typeof val === 'object' && !Array.isArray(val)) continue;
    const str = Array.isArray(val) ? val.join(', ') : String(val);
    if (!str.trim() || str.length > 200) continue;
    const label = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
    fields.push({ label, value: str });
  }
  return fields;
}

const GenericHoverCard: React.FC<{ entity: any }> = ({ entity }) => {
  const { getEntityIcon } = useEntityIcon({ size: 20 });
  const icon = getEntityIcon(entity);
  const [copied, setCopied] = useState(false);

  const typeLabel = entity.entityType
    ? entity.entityType.split('.').pop()
    : 'Entity';

  const skeletonFields = entity.skeleton
    ? readableSkeletonFields(entity.skeleton)
    : [];

  const cap = getCapability(entity.entityType || '');

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (entity._id) {
      useCommandRegistryStore.getState()
        .executeCommand('space_open_window', { entityId: entity._id })
        .catch(() => {});
    }
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const url = entityLinkService.getShareUrl(entity, { absolute: true });
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <div className="entity-hover-card__generic">
      <div className="entity-hover-card__generic-header">
        <span className="entity-hover-card__generic-icon">{icon}</span>
        <div className="entity-hover-card__generic-title">
          <span className="entity-hover-card__generic-name">{entity.name || '(Unnamed)'}</span>
          <span className="entity-hover-card__generic-type">{typeLabel}</span>
        </div>
      </div>
      {skeletonFields.length > 0 && (
        <div className="entity-hover-card__generic-fields">
          {skeletonFields.map(({ label, value }) => (
            <div key={label} className="entity-hover-card__generic-field">
              <span className="entity-hover-card__generic-field-label">{label}</span>
              <span className="entity-hover-card__generic-field-value">{value}</span>
            </div>
          ))}
        </div>
      )}
      <div className="entity-hover-card__actions">
        <button className="entity-hover-card__open-btn" onClick={handleOpen}>
          <ArrowSquareOut size={12} weight="bold" />
          Open
        </button>
        {cap.canBeSharedViaLink && (
          <button className="entity-hover-card__copy-link-btn" onClick={handleCopyLink} title="Copy shareable link">
            <Link size={12} weight="bold" />
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// EntityHoverCard
// ---------------------------------------------------------------------------

interface EntityHoverCardProps {
  entityId: string;
  entityType?: string;
  preloadedEntity?: any;
  anchor: HTMLElement;
  onClose: () => void;
  onCardMouseEnter?: () => void;
  onCardMouseLeave?: () => void;
}

export const EntityHoverCard: React.FC<EntityHoverCardProps> = ({
  entityId,
  entityType,
  preloadedEntity,
  anchor,
  onClose,
  onCardMouseEnter,
  onCardMouseLeave,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const [entity, setEntity] = useState<any>(preloadedEntity ?? null);
  const [loading, setLoading] = useState(!preloadedEntity);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Fetch entity if not preloaded
  useEffect(() => {
    if (preloadedEntity) return;
    let cancelled = false;
    setLoading(true);
    entityService.get(entityId)
      .then((e) => { if (!cancelled) { setEntity(e); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityId, preloadedEntity]);

  // Dismiss on outside click or Escape
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(e.target as Node) &&
        !anchor.contains(e.target as Node)
      ) {
        onCloseRef.current();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anchor]);

  // Position the card after it renders so we know its actual height.
  // Start invisible (opacity 0) to avoid a flash before positioning.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const anchorRect = anchor.getBoundingClientRect();
    const cardHeight = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: align to anchor left, clamp to viewport edges
    let x = anchorRect.left;
    if (x + CARD_WIDTH > vw - PADDING) x = vw - CARD_WIDTH - PADDING;
    if (x < PADDING) x = PADDING;

    // Vertical: prefer above, fall back to below if not enough room
    const spaceAbove = anchorRect.top - GAP;
    const spaceBelow = vh - anchorRect.bottom - GAP;
    let y: number;
    if (spaceAbove >= cardHeight || spaceAbove >= spaceBelow) {
      y = anchorRect.top - GAP - cardHeight;
      if (y < PADDING) y = PADDING;
    } else {
      y = anchorRect.bottom + GAP;
      if (y + cardHeight > vh - PADDING) y = vh - cardHeight - PADDING;
    }

    card.style.left = `${x}px`;
    card.style.top = `${y}px`;
    card.style.opacity = '1';
  });

  const resolvedType = entity?.entityType ?? entityType;
  const renderer = resolvedType ? entityHoverCardRegistry.getRenderer(resolvedType) : null;

  const renderContent = () => {
    if (loading) {
      if (renderer?.renderLoading) return renderer.renderLoading();
      return <div className="entity-hover-card__loading"><span className="entity-hover-card__loading-dots" /></div>;
    }
    if (!entity) return null;
    if (renderer) return renderer.render(entity);
    return <GenericHoverCard entity={entity} />;
  };

  const content = renderContent();
  if (!loading && !content) return null;

  return createPortal(
    <div
      ref={cardRef}
      className="entity-hover-card"
      style={{ position: 'fixed', left: 0, top: 0, width: CARD_WIDTH, opacity: 0 }}
      onMouseEnter={onCardMouseEnter}
      onMouseLeave={onCardMouseLeave}
    >
      {content}
    </div>,
    document.body,
  );
};
